import { apiRequest } from '../api';
import { navigateTo } from '../router';
import { listRefs, type GameMap, type GameMod } from '../api/game-refs';
import type { PlayerWithStats, PlayersListResponse, Race } from '../types';
import { t } from '../i18n';
import { state } from '../state';
import { buildOverlayToggle, showOverlayPrediction, startOverlayPredictionDraw, type MatchPredictOutput } from '../ui/overlay-output';
import { animateDrawRows } from '../ui/draw-anim';
import type { TranslationKey } from '../i18n/types';
import terranIconUrl from '../../assets/race/terran.svg';
import zergIconUrl from '../../assets/race/zerg.svg';
import protossIconUrl from '../../assets/race/protoss.svg';
import randomIconUrl from '../../assets/race/random.svg';

// ============================================================
// Раздел «Инструменты»: хаб и под-страница «Прогноз победителя».
// ============================================================

const RACE_ICON_URL: Record<string, string> = {
    T: terranIconUrl,
    Z: zergIconUrl,
    P: protossIconUrl,
    R: randomIconUrl,
};

const RACES: Race[] = ['T', 'Z', 'P', 'R'];

let cachedPlayers: PlayerWithStats[] = [];
let cachedMaps: GameMap[] = [];
let cachedMods: GameMod[] = [];

interface SelectedPlayer {
    id: number | null;
    name: string;
    race: Race | null;
}

interface PredictDraft {
    players: SelectedPlayer[];
    map_id: number | null;
    mod_id: number | null;
}

let selectedPlayers: SelectedPlayer[] = [];

const STORAGE_KEY = 'tools.predict.draft';

function saveDraft(): void {
    const mapSelect = document.getElementById('predict-map') as HTMLSelectElement | null;
    const modSelect = document.getElementById('predict-mod') as HTMLSelectElement | null;
    const draft: PredictDraft = {
        players: selectedPlayers,
        map_id: mapSelect?.value ? Number(mapSelect.value) : null,
        mod_id: modSelect?.value ? Number(modSelect.value) : null,
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
        /* localStorage недоступен — пропускаем */
    }
}

function loadDraft(): PredictDraft | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw) as PredictDraft;
        if (!d || !Array.isArray(d.players)) return null;
        return d;
    } catch {
        return null;
    }
}

// ============================================================
// Хаб «Инструменты»
// ============================================================
export function mountTools(): void {
    showScreen('screen-tools');

    const list = document.getElementById('tools-list');
    if (!list) return;
    list.innerHTML = '';

    const items = [
        buildToolsItem('/tools/match', '⚔️', 'tools.match_title', 'tools.match_desc', 'is-match', 'tools.tag_match'),
        buildToolsItem('/tools/predict', '🔮', 'tools.predict_title', 'tools.predict_desc', 'is-predict', 'tools.tag_predict'),
        buildToolsItem('/tools/random', '🎰', 'tools.random_title', 'tools.random_desc', 'is-random', 'tools.tag_random'),
    ];

    // «Режим стримера» — только для авторизованных (/control требует входа)
    if (state.token) {
        items.unshift(
            buildToolsItem('/control', '🎬', 'topbar.streamer_mode', 'tools.streamer_desc', 'is-streamer', 'tools.tag_streamer')
        );
    }

    list.append(...items);
}

/** Карточка-ссылка инструмента в хабе. */
function buildToolsItem(
    href: string,
    emoji: string,
    titleKey: TranslationKey,
    descKey: TranslationKey,
    kind: string,
    tagKey: TranslationKey
): HTMLElement {
    const item = document.createElement('a');
    item.href = href;
    item.className = `tools-item ${kind}`;

    const icon = document.createElement('div');
    icon.className = 'tools-item-icon';
    icon.textContent = emoji;

    const tag = document.createElement('div');
    tag.className = 'tools-item-tag';
    tag.dataset.i18n = tagKey;
    tag.textContent = t(tagKey);

    const body = document.createElement('div');
    body.className = 'tools-item-body';

    const name = document.createElement('div');
    name.className = 'tools-item-name';
    name.dataset.i18n = titleKey;
    name.textContent = t(titleKey);

    const desc = document.createElement('div');
    desc.className = 'tools-item-desc';
    desc.dataset.i18n = descKey;
    desc.textContent = t(descKey);

    body.append(name, desc);

    const cta = document.createElement('div');
    cta.className = 'tools-item-cta';
    const ctaText = document.createElement('span');
    ctaText.dataset.i18n = 'tools.open';
    ctaText.textContent = t('tools.open');
    const arrow = document.createElement('span');
    arrow.className = 'tools-item-arrow';
    arrow.textContent = '→';
    cta.append(ctaText, arrow);

    const top = document.createElement('div');
    top.className = 'tools-item-top';
    top.append(icon, tag);

    item.append(top, body, cta);
    return item;
}

export function unmountTools(): void {
    /* нет ресурсов для разблокировки */
}

// ============================================================
// Под-страница «Прогноз победителя»
// ============================================================
export async function mountPredict(): Promise<void> {
    showScreen('screen-predict');

    const backBtn = document.getElementById('btn-back-tools');
    if (backBtn) {
        const fresh = backBtn.cloneNode(true) as HTMLElement;
        backBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => navigateTo('/tools', true));
    }

    const errBox = document.getElementById('predict-error');
    const results = document.getElementById('predict-results');
    if (errBox) errBox.classList.add('hidden');
    if (results) results.classList.remove('hidden');

    // Восстанавливаем сохранённый черновик (если есть)
    const draft = loadDraft();
    selectedPlayers =
        draft && draft.players.length >= 1
            ? draft.players
            : [
                  { id: null, name: '', race: null },
                  { id: null, name: '', race: null },
              ];

    await loadRefs(draft);

    bindAddPlayer();
    bindRun();
    bindReset();
    bindMapModChange();
    bindRandomWinner();

    // Галочка вывода прогноза на OBS-оверлей (пересоздаём при каждом входе)
    const formCard = document.querySelector<HTMLElement>('#screen-predict .predict-form-card');
    if (formCard) {
        formCard.querySelector('.rr-overlay-toggle')?.remove();
        const toggle = buildOverlayToggle((on) => {
            if (on) pushPredictOverlay();
        });
        toggle.classList.add('predict-overlay-toggle');
        formCard.appendChild(toggle);
    }

    renderPlayerRows();
    renderResults([]);

    // Клик вне переключателя расы закрывает открытое меню
    document.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.race-picker')) return;
        document.querySelectorAll('.race-picker-menu').forEach((m) => m.remove());
    });
}

export function unmountPredict(): void {
    selectedPlayers = [];
}

function showScreen(id: string): void {
    document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
        el.classList.toggle('hidden', el.id !== id);
    });
}

async function loadRefs(draft?: PredictDraft | null): Promise<void> {
    try {
        const [players, maps, mods] = await Promise.all([
            apiRequest<PlayersListResponse>('/api/players').then((r) => r.players),
            listRefs<GameMap>('maps'),
            listRefs<GameMod>('mods'),
        ]);
        cachedPlayers = players;
        cachedMaps = maps;
        cachedMods = mods;
    } catch (err) {
        console.error('[tools] failed to load refs:', err);
    }

    // Заполняем селекты карт/модов («Не указано» — выбираемый пустой вариант)
    const mapSelect = document.getElementById('predict-map') as HTMLSelectElement | null;
    const modSelect = document.getElementById('predict-mod') as HTMLSelectElement | null;
    fillSelect(mapSelect, cachedMaps, t('tools.map_not_specified'));
    fillSelect(modSelect, cachedMods, t('tools.map_not_specified'));

    // Восстанавливаем выбранные карту/мод из черновика
    if (mapSelect && draft?.map_id) mapSelect.value = String(draft.map_id);
    if (modSelect && draft?.mod_id) modSelect.value = String(draft.mod_id);
}

function fillSelect(
    select: HTMLSelectElement | null,
    items: { id: number; name: string }[],
    placeholder: string
): void {
    if (!select) return;
    select.innerHTML = '';
    const empty = document.createElement('option');
    empty.value = '';
    empty.selected = true;
    empty.textContent = placeholder;
    select.appendChild(empty);
    for (const item of items) {
        const opt = document.createElement('option');
        opt.value = String(item.id);
        opt.textContent = item.name;
        select.appendChild(opt);
    }
}

// ============================================================
// Список игроков: строка в стиле формы игры + раса
// ============================================================
function renderPlayerRows(): void {
    const box = document.getElementById('predict-players');
    if (!box) return;

    box.innerHTML = '';
    selectedPlayers.forEach((sp, index) => {
        box.appendChild(buildPlayerRow(sp, index));
    });
}

function buildPlayerRow(sp: SelectedPlayer, index: number): HTMLElement {
    const row = document.createElement('div');
    row.className = 'player-entry predict-player-row';

    // --- Автокомплит игрока ---
    const autocompleteWrap = document.createElement('div');
    autocompleteWrap.className = 'player-autocomplete';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'player-name-input';
    input.placeholder = t('games.player_placeholder');
    input.autocomplete = 'off';
    input.value = sp.name;

    const dropdown = document.createElement('div');
    dropdown.className = 'player-autocomplete-dropdown hidden';

    autocompleteWrap.append(input, dropdown);
    row.appendChild(autocompleteWrap);

    let highlightedIndex = -1;
    let matches: PlayerWithStats[] = [];

    function showDropdown(query: string): void {
        const q = query.trim().toLowerCase();
        if (!q) {
            dropdown.classList.add('hidden');
            matches = [];
            return;
        }
        matches = cachedPlayers
            .filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    (p.aka ?? '').toLowerCase().includes(q)
            )
            .slice(0, 10);

        if (matches.length === 0) {
            dropdown.innerHTML = `<div class="player-autocomplete-empty">${t('games.player_not_found')}</div>`;
            dropdown.classList.remove('hidden');
            return;
        }

        dropdown.innerHTML = '';
        matches.forEach((p, i) => {
            const d = document.createElement('div');
            d.className = 'player-autocomplete-item';
            if (i === highlightedIndex) d.classList.add('is-highlighted');
            const name = document.createElement('span');
            name.className = 'player-autocomplete-name';
            name.textContent = p.name;
            d.appendChild(name);
            if (p.aka) {
                const aka = document.createElement('span');
                aka.className = 'player-autocomplete-aka';
                aka.textContent = ` aka ${p.aka}`;
                d.appendChild(aka);
            }
            d.addEventListener('mousedown', (e) => {
                e.preventDefault();
                selectPlayer(p);
            });
            dropdown.appendChild(d);
        });
        dropdown.classList.remove('hidden');
    }

    function selectPlayer(p: PlayerWithStats): void {
        // Автоподстановка доминирующей расы (как в форме игр), если ещё не выбрана
        const race = sp.race ?? p.dominant_race ?? null;
        selectedPlayers[index] = { id: p.id, name: p.name, race };
        raceControl.setRace(race);
        input.value = p.name;
        dropdown.classList.add('hidden');
        highlightedIndex = -1;
        saveDraft();
    }

    input.addEventListener('input', () => {
        selectedPlayers[index] = { id: null, name: input.value, race: null };
        highlightedIndex = -1;
        showDropdown(input.value);
        saveDraft();
    });

    input.addEventListener('focus', () => {
        if (input.value.trim()) showDropdown(input.value);
    });

    input.addEventListener('blur', () => {
        setTimeout(() => dropdown.classList.add('hidden'), 150);
    });

    input.addEventListener('keydown', (e) => {
        if (dropdown.classList.contains('hidden')) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            highlightedIndex = Math.min(highlightedIndex + 1, matches.length - 1);
            showDropdown(input.value);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlightedIndex = Math.max(highlightedIndex - 1, -1);
            showDropdown(input.value);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const picked = matches[highlightedIndex];
            if (picked) selectPlayer(picked);
        } else if (e.key === 'Escape') {
            dropdown.classList.add('hidden');
        }
    });

    // --- Раса (переключатель как в форме игры, с вариантом «не выбрано») ---
    const raceControl = buildRacePicker(sp.race, (race) => {
        selectedPlayers[index] = { ...selectedPlayers[index]!, race };
        saveDraft();
    });
    row.appendChild(raceControl.element);

    // --- Удалить ---
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'remove-player';
    removeBtn.textContent = '✕';
    removeBtn.title = t('games.remove_player');
    removeBtn.addEventListener('click', () => {
        selectedPlayers.splice(index, 1);
        renderPlayerRows();
        saveDraft();
    });
    row.appendChild(removeBtn);

    return row;
}

function buildRaceIcon(race: string, className: string): HTMLImageElement {
    const img = document.createElement('img');
    img.className = className;
    img.src = RACE_ICON_URL[race] ?? randomIconUrl;
    img.alt = race;
    img.loading = 'lazy';
    return img;
}

/**
 * Переключатель расы (как в форме добавления игры), но с опцией
 * «не выбрано» — расу можно оставить пустой.
 * Возвращает элемент и функцию setRace, чтобы снаружи (при выборе игрока)
 * можно было обновить триггер.
 */
export function buildRacePicker(
    initial: Race | null,
    onChange: (race: Race | null) => void
): { element: HTMLElement; setRace: (race: Race | null) => void } {
    const picker = document.createElement('div');
    picker.className = 'race-picker';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'race-picker-trigger';

    let currentRace: Race | null = initial;

    const setTrigger = (race: Race | null): void => {
        currentRace = race;
        trigger.innerHTML = '';
        if (race === null) {
            const q = document.createElement('span');
            q.className = 'race-picker-empty';
            q.textContent = '?';
            trigger.appendChild(q);
            trigger.title = t('tools.race_none');
        } else {
            trigger.appendChild(buildRaceIcon(race, 'race-picker-icon'));
            trigger.title = race;
        }
    };
    setTrigger(initial);

    let menu: HTMLDivElement | null = null;
    const closeMenu = (): void => {
        menu?.remove();
        menu = null;
    };

    const openMenu = (): void => {
        closeMenu();
        // Закрываем чужие открытые меню рас
        document.querySelectorAll('.race-picker-menu').forEach((m) => m.remove());

        menu = document.createElement('div');
        menu.className = 'race-picker-menu';

        const options: (Race | null)[] = [null, ...RACES];
        const slotH = 34;
        const gap = 4;
        for (const r of options) {
            const slot = document.createElement('button');
            slot.type = 'button';
            slot.className =
                'race-picker-slot' +
                (r === null ? ' is-none' : '') +
                (currentRace === r ? ' is-active' : '');
            if (r === null) {
                slot.textContent = '—';
                slot.title = t('tools.race_none');
            } else {
                slot.appendChild(buildRaceIcon(r, 'race-picker-icon'));
                slot.title = r;
            }
            if (currentRace === r) {
                // Выбранную расу блокируем — её нельзя выбрать повторно
                slot.disabled = true;
            } else {
                slot.addEventListener('click', (e) => {
                    e.stopPropagation();
                    setTrigger(r);
                    onChange(r);
                    closeMenu();
                });
            }
            menu.appendChild(slot);
        }

        const tr = trigger.getBoundingClientRect();
        const menuW = 40;
        const menuH = options.length * slotH + (options.length - 1) * gap + 8;
        let top = tr.bottom + 6;
        let left = tr.left + tr.width / 2 - menuW / 2;
        top = Math.max(8, Math.min(top, window.innerHeight - menuH - 8));
        left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
        menu.style.top = `${top}px`;
        menu.style.left = `${left}px`;

        document.body.appendChild(menu);
    };

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menu) closeMenu();
        else openMenu();
    });

    picker.appendChild(trigger);

    return {
        element: picker,
        setRace: (race) => setTrigger(race),
    };
}

function bindAddPlayer(): void {
    const btn = document.getElementById('btn-predict-add-player');
    if (!btn) return;
    const fresh = btn.cloneNode(true) as HTMLElement;
    btn.replaceWith(fresh);
    fresh.addEventListener('click', () => {
        selectedPlayers.push({ id: null, name: '', race: null });
        renderPlayerRows();
        saveDraft();
    });
}

function bindReset(): void {
    const btn = document.getElementById('btn-predict-reset');
    if (!btn) return;
    const fresh = btn.cloneNode(true) as HTMLElement;
    btn.replaceWith(fresh);
    fresh.addEventListener('click', () => {
        localStorage.removeItem(STORAGE_KEY);
        selectedPlayers = [
            { id: null, name: '', race: null },
            { id: null, name: '', race: null },
        ];
        const mapSelect = document.getElementById('predict-map') as HTMLSelectElement | null;
        const modSelect = document.getElementById('predict-mod') as HTMLSelectElement | null;
        if (mapSelect) mapSelect.value = '';
        if (modSelect) modSelect.value = '';
        const results = document.getElementById('predict-results');
        const err = document.getElementById('predict-error');
        if (results) results.classList.remove('hidden');
        if (err) err.classList.add('hidden');
        renderResults([]);
        renderPlayerRows();
    });
}

function bindMapModChange(): void {
    const mapSelect = document.getElementById('predict-map') as HTMLSelectElement | null;
    const modSelect = document.getElementById('predict-mod') as HTMLSelectElement | null;
    mapSelect?.addEventListener('change', saveDraft);
    modSelect?.addEventListener('change', saveDraft);
}

// ============================================================
// Запуск прогноза и отрисовка результата
// ============================================================
function bindRun(): void {
    const btn = document.getElementById('btn-predict-run');
    if (!btn) return;
    const fresh = btn.cloneNode(true) as HTMLElement;
    btn.replaceWith(fresh);
    fresh.addEventListener('click', () => void runPrediction(fresh));
}

async function runPrediction(btn: HTMLElement): Promise<void> {
    const errBox = document.getElementById('predict-error');
    const results = document.getElementById('predict-results');
    if (errBox) errBox.classList.add('hidden');

    const playerIds = selectedPlayers
        .map((p) => p.id)
        .filter((id): id is number => id !== null);

    if (playerIds.length < 2) {
        if (errBox) {
            errBox.textContent = t('tools.predict_need_two');
            errBox.classList.remove('hidden');
        }
        return;
    }

    const mapSelect = document.getElementById('predict-map') as HTMLSelectElement | null;
    const modSelect = document.getElementById('predict-mod') as HTMLSelectElement | null;
    const mapId = mapSelect?.value ? Number(mapSelect.value) : null;
    const modId = modSelect?.value ? Number(modSelect.value) : null;

    const original = btn.textContent;
    btn.textContent = t('common.loading');
    btn.setAttribute('disabled', '');

    // Пока считаем — та же колонка с пульсирующим скелетом, без пустоты
    const body = document.getElementById('predict-results-body');
    if (body) renderResultsPlaceholder(body, true);
    if (results) results.classList.remove('hidden');

    try {
        const rows = await fetchPrediction(playerIds, mapId, modId);

        if (results) results.classList.remove('hidden');
        renderResults(rows);
        // Автоматически разыгрываем предварительного победителя сразу после расчёта
        drawRandomWinner();
    } catch (err) {
        if (body) renderResultsPlaceholder(body);
        if (errBox) {
            errBox.textContent =
                t('tools.predict_error') +
                ' ' +
                (err instanceof Error ? err.message : String(err));
            errBox.classList.remove('hidden');
        }
    } finally {
        btn.textContent = original;
        btn.removeAttribute('disabled');
    }
}

export interface PredictResultRow {
    player_id: number;
    name: string;
    place: number;
    win_chance: number;
    games_played: number | null;
    winrate: number | null;
    avg_place: number | null;
    map_games: number | null;
    map_winrate: number | null;
    map_avg_place: number | null;
    mod_games: number | null;
    mod_winrate: number | null;
    mod_avg_place: number | null;
}

/** Запрос прогноза победителя — переиспользуется в «Менеджере матча». */
export async function fetchPrediction(
    playerIds: number[],
    mapId: number | null,
    modId: number | null
): Promise<PredictResultRow[]> {
    const res = await apiRequest<{ results: PredictResultRow[] }>('/api/tools/predict', {
        method: 'POST',
        body: { player_ids: playerIds, map_id: mapId, mod_id: modId },
    });
    return res.results;
}

// Последний рассчитанный прогноз и id разыгранного предварительного победителя
let lastResults: PredictResultRow[] = [];
let drawnWinnerId: number | null = null;

function bindRandomWinner(): void {
    const btn = document.getElementById('btn-predict-random');
    if (!btn) return;
    const fresh = btn.cloneNode(true) as HTMLElement;
    btn.replaceWith(fresh);
    fresh.addEventListener('click', drawRandomWinner);
}

/**
 * Разыгрывает предварительного победителя по рассчитанным шансам:
 * вероятность выпадения игрока пропорциональна его win_chance.
 * Возвращает id игрока (или null, если считать не из чего).
 */
export function drawWeightedWinner(rows: PredictResultRow[]): number | null {
    if (rows.length === 0) return null;

    const weights = rows.map((r) => Math.max(0, r.win_chance));
    const total = weights.reduce((a, b) => a + b, 0);

    if (total <= 0) {
        // Шансы нулевые — разыгрываем равновероятно
        return rows[Math.floor(Math.random() * rows.length)]!.player_id;
    }
    let r = Math.random() * total;
    for (let i = 0; i < rows.length; i++) {
        r -= weights[i] ?? 0;
        const row = rows[i];
        if (row && r <= 0) return row.player_id;
    }
    return rows[rows.length - 1]!.player_id;
}

/** Отмена идущей прокрутки — новый клик перебивает прошлый розыгрыш. */
let cancelDraw: (() => void) | null = null;

/**
 * Разыгрывает предварительного победителя с прокруткой подсветки:
 * строки «бегут» по кругу, замедляются и останавливаются на победителе.
 * Оверлей получает результат сразу и крутит ту же анимацию сам.
 */
function drawRandomWinner(): void {
    if (lastResults.length === 0) return;

    const winner = drawWeightedWinner(lastResults);
    const winnerIndex = winner === null ? -1 : lastResults.findIndex((r) => r.player_id === winner);
    if (winner === null || winnerIndex < 0) return;

    cancelDraw?.();
    // Строки без кубика, пока идёт прокрутка
    drawnWinnerId = null;
    renderResults(lastResults);
    // Оверлей: сначала прокрутка подсветки строк, в финале — кубик и автоскрытие
    pushPredictOverlay(winner, 'rolling');

    const body = document.getElementById('predict-results-body');
    if (!body) return;
    cancelDraw = animateDrawRows(body, '.predict-chart-row', lastResults.length, winnerIndex, () => {
        cancelDraw = null;
        drawnWinnerId = winner;
        renderResults(lastResults);
        pushPredictOverlay(winner, 'result');
    });
}

/** Данные прогноза для виджета оверлея (карта/мод + расы участников). */
function buildPredictOverlay(drawn: number | null = drawnWinnerId): MatchPredictOutput | null {
    if (lastResults.length < 2) return null;

    const mapSelect = document.getElementById('predict-map') as HTMLSelectElement | null;
    const modSelect = document.getElementById('predict-mod') as HTMLSelectElement | null;
    const mapId = mapSelect?.value ? Number(mapSelect.value) : null;
    const modId = modSelect?.value ? Number(modSelect.value) : null;

    const map = cachedMaps.find((m) => m.id === mapId);
    const mod = cachedMods.find((m) => m.id === modId);
    const races = new Map<string, Race | null>(
        selectedPlayers.map((p) => [p.name, p.race] as [string, Race | null])
    );

    return {
        mapName: map?.name ?? null,
        modName: mod?.name ?? null,
        players: lastResults.map((r) => ({
            name: r.name,
            race: races.get(r.name) ?? null,
            chance: r.win_chance,
            place: r.place,
            drawn: r.player_id === drawn,
            games: r.games_played,
            winrate: r.winrate,
        })),
    };
}

/** Отправляет текущий прогноз на оверлей (если галочка включена). */
function pushPredictOverlay(
    drawn: number | null = drawnWinnerId,
    phase: 'rolling' | 'result' = 'result'
): void {
    const data = buildPredictOverlay(drawn);
    if (!data) return;
    void (phase === 'rolling' ? startOverlayPredictionDraw(data) : showOverlayPrediction(data));
}

function renderResults(rows: PredictResultRow[]): void {
    const body = document.getElementById('predict-results-body');
    if (!body) return;

    if (rows.length === 0) {
        cancelDraw?.();
        cancelDraw = null;
        lastResults = [];
        drawnWinnerId = null;
        renderResultsPlaceholder(body);
        renderDrawnWinner();
        return;
    }

    lastResults = rows;
    setRandomBtnEnabled(true);

    // Сверху — компактный график шансов, снизу — пояснение по каждому игроку
    renderPredictResults(body, rows, drawnWinnerId);
    renderDrawnWinner();
}

/** Заглушка правой колонки: скелет строк, пока прогноза нет или он считается. */
function renderResultsPlaceholder(body: HTMLElement, loading = false): void {
    setRandomBtnEnabled(false);
    body.innerHTML = '';

    const wrap = document.createElement('div');
    wrap.className = 'predict-placeholder' + (loading ? ' is-loading' : '');

    const skeleton = document.createElement('div');
    skeleton.className = 'predict-placeholder-rows';
    skeleton.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 5; i++) {
        const row = document.createElement('div');
        row.className = 'predict-placeholder-row';

        const place = document.createElement('span');
        place.className = 'predict-placeholder-place';
        place.textContent = String(i + 1);

        const bar = document.createElement('span');
        bar.className = 'predict-placeholder-bar';
        bar.style.width = `${88 - i * 13}%`;

        row.append(place, bar);
        skeleton.appendChild(row);
    }

    const title = document.createElement('div');
    title.className = 'predict-placeholder-title';
    title.textContent = loading ? t('tools.predict_placeholder_loading') : t('tools.predict_placeholder_title');

    wrap.append(skeleton, title);

    if (!loading) {
        const hint = document.createElement('p');
        hint.className = 'predict-placeholder-text';
        hint.textContent = t('tools.predict_placeholder_text');
        wrap.appendChild(hint);
    }

    body.appendChild(wrap);
}

/** Кнопка «Перебросить победителя» имеет смысл только при готовом прогнозе. */
function setRandomBtnEnabled(enabled: boolean): void {
    const btn = document.getElementById('btn-predict-random') as HTMLButtonElement | null;
    if (!btn) return;
    btn.disabled = !enabled;
}

/**
 * Рисует результат прогноза (график + пояснение) в произвольный контейнер.
 * Переиспользуется «Менеджером матча»; drawnWinner подсвечивает разыгранного.
 */
export function renderPredictResults(
    container: HTMLElement,
    rows: PredictResultRow[],
    drawnWinner: number | null
): void {
    container.innerHTML = '';
    if (rows.length === 0) return;
    container.appendChild(buildChancesChart(rows, drawnWinner));
    container.appendChild(buildDetails(rows, drawnWinner));
}

// ============================================================
// График шансов (сверху) — компактные строки: место, ник, полоса, %
// ============================================================
function buildChancesChart(rows: PredictResultRow[], drawnWinner: number | null): HTMLElement {
    const section = document.createElement('div');
    section.className = 'predict-section';

    const title = document.createElement('div');
    title.className = 'predict-section-title';
    title.textContent = t('tools.predict_chart_title');
    section.appendChild(title);

    const chart = document.createElement('div');
    chart.className = 'predict-chart';

    for (const r of rows) {
        const row = document.createElement('div');
        row.className = 'predict-chart-row' + (r.place === 1 ? ' is-winner' : '');
        if (r.player_id === drawnWinner) row.classList.add('is-drawn');

        const rank = document.createElement('div');
        rank.className = 'predict-chart-rank';
        rank.textContent = String(r.place);
        row.appendChild(rank);

        const name = document.createElement('a');
        name.className = 'predict-chart-name';
        name.href = `/?player=${encodeURIComponent(r.name)}`;
        name.textContent = r.name;
        name.title = r.name;
        row.appendChild(name);

        const bar = document.createElement('div');
        bar.className = 'predict-chart-bar';
        const fill = document.createElement('div');
        fill.className = 'predict-chart-fill';
        fill.style.width = `${Math.max(2, Math.min(100, r.win_chance))}%`;
        bar.appendChild(fill);
        row.appendChild(bar);

        const pct = document.createElement('div');
        pct.className = 'predict-chart-pct';
        if (r.player_id === drawnWinner) {
            const dice = document.createElement('span');
            dice.className = 'predict-chart-dice';
            dice.textContent = '🎲';
            pct.appendChild(dice);
        }
        const pctText = document.createElement('span');
        pctText.textContent = `${r.win_chance}%`;
        pct.appendChild(pctText);
        row.appendChild(pct);

        chart.appendChild(row);
    }

    section.appendChild(chart);
    return section;
}

// ============================================================
// Пояснение (снизу) — из чего складываются шансы каждого игрока
// ============================================================
function buildDetails(rows: PredictResultRow[], drawnWinner: number | null): HTMLElement {
    const section = document.createElement('div');
    section.className = 'predict-section';

    const title = document.createElement('div');
    title.className = 'predict-section-title';
    title.textContent = t('tools.predict_details_title');
    section.appendChild(title);

    const list = document.createElement('div');
    list.className = 'predict-list';
    for (const r of rows) {
        list.appendChild(buildDetailRow(r, drawnWinner));
    }
    section.appendChild(list);
    return section;
}

function buildDetailRow(r: PredictResultRow, drawnWinner: number | null): HTMLElement {
    const row = document.createElement('div');
    row.className = 'predict-row' + (r.place === 1 ? ' is-winner' : '');
    if (r.player_id === drawnWinner) row.classList.add('is-drawn');

    // Место
    const place = document.createElement('div');
    place.className = 'predict-row-place';
    place.textContent = String(r.place);
    row.appendChild(place);

    // Основная часть
    const main = document.createElement('div');
    main.className = 'predict-row-main';

    const name = document.createElement('a');
    name.className = 'predict-row-name';
    name.href = `/?player=${encodeURIComponent(r.name)}`;
    name.textContent = r.name;
    main.appendChild(name);

    if (r.player_id === drawnWinner) {
        const badge = document.createElement('span');
        badge.className = 'predict-drawn-badge';
        badge.textContent = `🎲 ${t('tools.predict_random_winner')}`;
        main.appendChild(badge);
    }

    // Общая статистика — чипы
    const stats = document.createElement('div');
    stats.className = 'predict-row-stats';
    if (r.games_played !== null && r.games_played !== undefined) {
        stats.appendChild(chip(`${r.games_played} ${t('player.games_games_word')}`));
    }
    if (r.winrate !== null && r.winrate !== undefined) {
        stats.appendChild(chip(`${r.winrate}%`));
    }
    if (r.avg_place !== null && r.avg_place !== undefined) {
        stats.appendChild(chip(`${t('tools.result_avg_place')} ${r.avg_place}`));
    }
    main.appendChild(stats);

    // Контекст карты/мода — отдельные блоки с подписью
    if (r.map_games !== null && r.map_games !== undefined) {
        main.appendChild(
            contextBlock(t('tools.result_map'), 'map', [
                `${r.map_games} ${t('player.games_games_word')}`,
                r.map_winrate !== null && r.map_winrate !== undefined
                    ? `${r.map_winrate}%`
                    : null,
                r.map_avg_place !== null && r.map_avg_place !== undefined
                    ? `${t('tools.result_avg_place')} ${r.map_avg_place}`
                    : null,
            ])
        );
    }
    if (r.mod_games !== null && r.mod_games !== undefined) {
        main.appendChild(
            contextBlock(t('tools.result_mod'), 'mod', [
                `${r.mod_games} ${t('player.games_games_word')}`,
                r.mod_winrate !== null && r.mod_winrate !== undefined
                    ? `${r.mod_winrate}%`
                    : null,
                r.mod_avg_place !== null && r.mod_avg_place !== undefined
                    ? `${t('tools.result_avg_place')} ${r.mod_avg_place}`
                    : null,
            ])
        );
    }

    row.appendChild(main);

    // Шанс на победу
    const chance = document.createElement('div');
    chance.className = 'predict-row-chance';
    const bar = document.createElement('div');
    bar.className = 'predict-chance-bar';
    const fill = document.createElement('div');
    fill.className = 'predict-chance-fill';
    fill.style.width = `${Math.max(2, Math.min(100, r.win_chance))}%`;
    bar.appendChild(fill);
    const label = document.createElement('span');
    label.className = 'predict-chance-label';
    label.textContent = `${r.win_chance}%`;
    chance.append(bar, label);
    row.appendChild(chance);

    return row;
}

/** Баннер с разыгранным предварительным победителем над списком. */
function renderDrawnWinner(): void {
    const box = document.getElementById('predict-random-winner');
    if (!box) return;

    const picked = lastResults.find((r) => r.player_id === drawnWinnerId);
    if (!picked) {
        box.classList.add('hidden');
        box.innerHTML = '';
        return;
    }

    box.innerHTML = '';
    const icon = document.createElement('span');
    icon.className = 'predict-random-winner-icon';
    icon.textContent = '🎲';

    const label = document.createElement('span');
    label.className = 'predict-random-winner-label';
    label.textContent = t('tools.predict_random_winner') + ':';

    const name = document.createElement('span');
    name.className = 'predict-random-winner-name';
    name.textContent = picked.name;

    const chance = document.createElement('span');
    chance.className = 'predict-random-winner-chance';
    chance.textContent = `${picked.win_chance}%`;

    box.append(icon, label, name, chance);
    box.classList.remove('hidden');
}

function chip(text: string): HTMLElement {
    const s = document.createElement('span');
    s.className = 'predict-chip';
    s.textContent = text;
    return s;
}

function contextBlock(
    label: string,
    kind: 'map' | 'mod',
    items: (string | null)[]
): HTMLElement {
    const block = document.createElement('div');
    block.className = `predict-context predict-context--${kind}`;

    const lbl = document.createElement('span');
    lbl.className = 'predict-context-label';
    lbl.textContent = label;
    block.appendChild(lbl);

    for (const it of items) {
        if (it === null) continue;
        block.appendChild(chip(it));
    }

    return block;
}